// The thread page (roadmap 3.3): its story, money, people, tasks and sub-threads, and the owner's
// corrections right here (rename, move, merge, done/closed, focus, an event to another thread).
import { useCallback, useEffect, useState } from 'react';
import { api, onChange } from '../api';
import { day, ft, HeatRing, Label, Panel, tc } from '../ui';

interface Ev { id: string; occurred_at: string; kind: string; actor: string | null; text: string; thread_id: string; confidence: number; method: string; amount: string | null; status: string | null }
interface Page {
  thread: { id: string; name: string; summary: string; next_step: string | null; temperature: string; heat: number; next_deadline: string | null; wake_reason: string | null;
    focus: boolean; stored_status: string; parent_id: string | null; parent_name: string | null; events: number };
  children: Array<{ id: string; name: string; temperature: string; heat: number; next_step: string | null; next_deadline: string | null; wake_reason: string | null; status: string; events: number; net: number | null }>;
  events: Ev[];
  money: { spent: number; received: number; net: number; payments: number };
  people: Array<{ id: string; name: string; type: string; n: number; last_at: string }>;
  tasks: Array<{ ref: string; title: string; due: string | null; status: string; overdue: boolean }>;
}
interface Lite { id: string; name: string; parent_id: string | null; status: string }

const KIND: Record<string, string> = { payment: 'fizetés', message: 'üzenet', task: 'teendő', exam: 'vizsga', class: 'óra', plan: 'terv', project: 'projekt', goal: 'cél', email: 'levél' };
const ACT: Record<string, string> = { done: 'kész', dismissed: 'elvetve', open: 'nyitott' };

export function Thread({ id }: { id: string }) {
  const [p, setP] = useState<Page | null>(null);
  const [all, setAll] = useState<Lite[]>([]);
  const [err, setErr] = useState('');
  const [mode, setMode] = useState<'' | 'rename' | 'parent' | 'merge'>('');
  const [val, setVal] = useState('');
  const [moving, setMoving] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);

  const load = useCallback(() => {
    api<Page>(`/api/threads/${id}`).then(setP).catch((e) => setErr(e.message));
    api<Lite[]>('/api/threads').then(setAll).catch(() => {});
  }, [id]);
  useEffect(() => { setP(null); setMode(''); load(); return onChange(load); }, [load]);

  const fix = (corrections: object[]) => api('/api/corrections', { corrections }).then(() => { setMode(''); setMoving(null); load(); }).catch((e) => setErr(e.message));
  if (err && !p) return <p className="err">{err}</p>;
  if (!p) return <p className="muted">Betöltés…</p>;
  const t = p.thread;
  const others = all.filter((x) => x.id !== t.id && x.status !== 'closed');
  const tops = others.filter((x) => !x.parent_id);
  const sorted = [...others].sort((a, b) => a.name.localeCompare(b.name, 'hu'));
  const evs = showAll ? p.events : p.events.slice(0, 40);

  return (
    <div className="j-stack thread-page" style={tc(t.id)}>
      {t.parent_name && <a className="crumb" href={`#/szal/${t.parent_id}`}>‹ {t.parent_name}</a>}
      <div className="thread-head">
        <HeatRing heat={t.heat} id={t.id} />
        <div>
          <div className="kind">{t.focus ? 'fókusz · ' : ''}{t.wake_reason || t.temperature}{t.next_deadline ? ` · ${day(t.next_deadline)}` : ''}</div>
          <h1 className="thread-title">{t.name}</h1>
        </div>
      </div>
      {t.summary && <p className="sum">{t.summary}</p>}
      {t.next_step && <Panel c="amber" className="j-next"><Label c="amber">Következő lépés</Label><h2>{t.next_step}</h2></Panel>}

      <div className="j-row fix-bar">
        <button className="j-btn" onClick={() => { setMode(mode === 'rename' ? '' : 'rename'); setVal(t.name); }}>Átnevezés</button>
        <button className="j-btn" onClick={() => { setMode(mode === 'parent' ? '' : 'parent'); setVal(t.parent_id ?? ''); }}>Áthelyezés</button>
        <button className="j-btn" onClick={() => { setMode(mode === 'merge' ? '' : 'merge'); setVal(''); }}>Összevonás</button>
        <button className="j-btn" onClick={() => fix([{ action: 'thread_focus', thread: t.id, focus: !t.focus }])}>{t.focus ? 'Nincs fókuszban' : 'Fókuszba'}</button>
        {t.stored_status === 'active'
          ? <><button className="j-btn" onClick={() => fix([{ action: 'thread_status', thread: t.id, status: 'done' }])}>Kész</button>
              <button className="j-btn" onClick={() => fix([{ action: 'thread_status', thread: t.id, status: 'closed' }])}>Lezárás</button></>
          : <button className="j-btn" onClick={() => fix([{ action: 'thread_status', thread: t.id, status: 'active' }])}>Újranyitás</button>}
      </div>
      {mode === 'rename' && (
        <div className="j-row"><input className="j-input" value={val} onChange={(e) => setVal(e.target.value)} />
          <button className="j-btn j-btn--primary" disabled={!val.trim()} onClick={() => fix([{ action: 'thread_rename', thread: t.id, name: val.trim() }])}>Mentés</button></div>
      )}
      {mode === 'parent' && (
        <div className="j-row"><select className="j-input" value={val} onChange={(e) => setVal(e.target.value)}>
          <option value="">— önálló nagy szál —</option>{tops.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}</select>
          <button className="j-btn j-btn--primary" onClick={() => fix([{ action: 'thread_parent', thread: t.id, parent: val || null }])}>Áthelyezés</button></div>
      )}
      {mode === 'merge' && (
        <div className="j-row"><select className="j-input" value={val} onChange={(e) => setVal(e.target.value)}>
          <option value="">Melyikbe vonjam össze?</option>{sorted.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}</select>
          <button className="j-btn j-btn--primary" disabled={!val} onClick={() => fix([{ action: 'thread_merge', from: t.id, into: val }]).then(() => { location.hash = `#/szal/${val}`; })}>Összevonás</button></div>
      )}

      {p.children.length > 0 && (
        <Panel c="cyan"><Label c="cyan">Alszálak</Label>
          {p.children.map((k) => (
            <a key={k.id} className={`j-thread child ${k.temperature === 'alvó' || k.status !== 'active' ? 'j-thread--cold' : ''}`} style={tc(t.id)} href={`#/szal/${k.id}`}>
              <HeatRing heat={k.heat} id={t.id} />
              <div><div className="kind">{k.wake_reason || (k.status === 'done' ? 'kész' : k.status === 'closed' ? 'lezárt' : k.temperature)}</div><h3>{k.name}</h3>{k.next_step && <div className="nx">{k.next_step}</div>}</div>
              <span className="when">{k.next_deadline ? day(k.next_deadline) : k.net ? ft(k.net) : ''}</span>
            </a>
          ))}
        </Panel>
      )}

      {(p.money.payments > 0 || p.people.length > 0) && (
        <div className="two">
          {p.money.payments > 0 && (
            <Panel c="mint" className="j-lens"><div className="k">Pénz</div><div className="v j-num">{ft(p.money.net)}</div>
              <div className="s">kiadás {ft(p.money.spent)} · bejött {ft(p.money.received)} · {p.money.payments} tétel</div></Panel>
          )}
          {p.people.length > 0 && (
            <Panel c="mint"><div className="j-lens"><div className="k">Emberek</div></div>
              <div className="chips">{p.people.map((x) => <span key={x.id} className="j-chip j-c-mint" title={`${x.n} esemény, utoljára ${day(x.last_at)}`}>{x.name}</span>)}</div></Panel>
          )}
        </div>
      )}

      {p.tasks.length > 0 && (
        <Panel c="coral"><Label c="coral">Teendők</Label>
          {p.tasks.map((x) => (
            <div key={x.ref} className={`j-slot ${x.status !== 'open' ? 'done' : ''}`}>
              <div className={`t ${x.overdue ? 'late' : ''}`}>{x.due ? day(x.due) : '–'}</div>
              <div><div>{x.title}</div><div className="w">{ACT[x.status] ?? x.status}</div></div>
            </div>
          ))}
        </Panel>
      )}

      <Label c="cyan">Történet · {p.events.length}</Label>
      <Panel c="cyan">
        {evs.map((e) => (
          <div key={e.id + e.thread_id} className={`j-slot ev ${e.confidence < 0.8 && e.method !== 'owner' ? 'unsure' : ''}`}>
            <div><div className="when">{day(e.occurred_at)}</div><div className="src">{KIND[e.kind] ?? e.kind}</div></div>
            <div>
              <div className="ev-text">{e.text}</div>
              {e.thread_id !== t.id && <div className="w">{p.children.find((k) => k.id === e.thread_id)?.name}</div>}
              {moving === e.id ? (
                <div className="j-row ev-move">
                  <select className="j-input" defaultValue="" onChange={(ev) => ev.target.value && fix([{ action: 'event_move', event: e.id, from: e.thread_id, to: ev.target.value === '-' ? null : ev.target.value }])}>
                    <option value="">Hová tartozik?</option><option value="-">Egyik szálhoz sem</option>
                    {sorted.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
                  </select>
                  <button className="j-btn" onClick={() => setMoving(null)}>Mégse</button>
                </div>
              ) : (
                <div className="ev-acts">
                  {e.confidence < 0.8 && e.method !== 'owner' && <button className="j-btn" onClick={() => fix([{ action: 'event_confirm', event: e.id, thread: e.thread_id }])}>Jó helyen van</button>}
                  <button className="j-btn" onClick={() => setMoving(e.id)}>Máshová</button>
                </div>
              )}
            </div>
          </div>
        ))}
        {!showAll && p.events.length > 40 && <button className="j-btn" onClick={() => setShowAll(true)}>Mind a {p.events.length}</button>}
      </Panel>
      {err && <p className="err">{err}</p>}
    </div>
  );
}

/** All threads as a tree (the "Szálak" tab). */
export function Threads() {
  const [all, setAll] = useState<Array<Lite & { temperature: string; heat: number; next_deadline: string | null; wake_reason: string | null; net: number | null; events: number }>>([]);
  const [showClosed, setShowClosed] = useState(false);
  useEffect(() => { const l = () => api('/api/threads').then(setAll).catch(() => {}); l(); return onChange(l); }, []);
  const tops = all.filter((x) => !x.parent_id && (showClosed || x.status === 'active'));
  const kids = (id: string) => all.filter((x) => x.parent_id === id && (showClosed || x.status === 'active'));
  return (
    <div className="j-stack">
      <Label c="cyan">Szálak · {tops.length}</Label>
      {tops.map((t) => (
        <div key={t.id} className="thread-group">
          <a className={`j-panel j-thread ${t.temperature === 'alvó' || t.status !== 'active' ? 'j-thread--cold' : ''}`} style={tc(t.id)} href={`#/szal/${t.id}`}>
            <HeatRing heat={t.heat} id={t.id} />
            <div><div className="kind">{t.wake_reason || t.temperature}</div><h3>{t.name}</h3></div>
            <span className="when">{t.next_deadline ? day(t.next_deadline) : t.net ? ft(t.net) : ''}</span>
          </a>
          {kids(t.id).map((k) => <a key={k.id} className="sub-thread" href={`#/szal/${k.id}`} style={tc(t.id)}><span className="dot" /> {k.name}<em> · {k.wake_reason || k.temperature}</em></a>)}
        </div>
      ))}
      <button className="j-btn linkish" onClick={() => setShowClosed(!showClosed)}>{showClosed ? 'Csak az aktívak' : 'A lezártak is'}</button>
    </div>
  );
}
